import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

const STORAGE_KEY = 'agustinos_push_history';
const MAX_NOTIFICATIONS = 50;
const MAX_AGE_DAYS = 30;

class PushNotificationService {
  constructor() {
    this.isNativePlatform = Capacitor.isNativePlatform();
  }

  /**
   * Inicializar el servicio de notificaciones push
   */
  async initialize() {
    if (!this.isNativePlatform) {
      console.log('📱 Push notifications solo disponibles en apps nativas');
      return;
    }

    console.log('🔔 Inicializando Push Notifications...');

    try {
      // Registrar listeners
      await this.registerListeners();

      // Solicitar permiso
      const permStatus = await PushNotifications.requestPermissions();
      
      if (permStatus.receive === 'granted') {
        console.log('✅ Permisos de notificaciones concedidos');
        await PushNotifications.register();
        await this.syncDeliveredNotifications();
        setTimeout(() => this.syncDeliveredNotifications(), 3000);
        
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            this.syncDeliveredNotifications();
          }
        });
      } else {
        console.log('❌ Permisos de notificaciones denegados');
      }
    } catch (error) {
      console.error('❌ Error inicializando push notifications:', error);
    }
  }

  /**
   * Capturar notificaciones que llegaron mientras la app estaba cerrada/background
   */
  async syncDeliveredNotifications() {
    try {
      const delivered = await PushNotifications.getDeliveredNotifications();
      if (delivered.notifications && delivered.notifications.length > 0) {
        console.log(`📬 ${delivered.notifications.length} notificación(es) pendiente(s) encontrada(s)`);
        delivered.notifications.forEach(notification => {
          this.saveNotification(notification, false);
        });
      }
    } catch (error) {
      console.error('Error sincronizando notificaciones pendientes:', error);
    }
  }

  /**
   * Registrar todos los listeners de notificaciones
   */
  async registerListeners() {
    // Listener: Registro exitoso
    await PushNotifications.addListener('registration', (token) => {
      console.log('✅ Push registration success, token:', token.value);
      
      // Guardar token en localStorage para enviarlo al backend
      localStorage.setItem('fcm_token', token.value);
      
      // Aquí puedes enviar el token a tu backend
      this.sendTokenToBackend(token.value);
    });

    // Listener: Error en registro
    await PushNotifications.addListener('registrationError', (error) => {
      console.error('❌ Error en registro de push:', error);
    });

    // Listener: Notificación recibida (app en foreground)
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('📬 Notificación recibida en foreground:', notification);
      
      this.saveNotification(notification, false);
      this.handleForegroundNotification(notification);
    });

    // Listener: Usuario toca la notificación (app en background)
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('👆 Usuario tocó notificación:', action);
      
      this.saveNotification(action.notification, true);
      this.handleNotificationTap(action.notification);
    });
  }

  /**
   * Enviar token FCM al backend
   */
  async sendTokenToBackend(token) {
    try {
      console.log('📤 Enviando token al backend...');
      
      const headers = { 'Content-Type': 'application/json' };

      // Si el usuario está autenticado, incluir el JWT (opcional)
      const userToken = localStorage.getItem('token');
      if (userToken) {
        headers['Authorization'] = `Bearer ${userToken}`;
      }

      const response = await fetch('https://api.appagustinos.es/api/notifications/register-token', {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          token, 
          platform: Capacitor.getPlatform(),
          deviceInfo: {
            platform: Capacitor.getPlatform(),
            isNative: Capacitor.isNativePlatform()
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Token registrado en backend');
      } else {
        console.error('❌ Error del servidor:', data.error);
      }
    } catch (error) {
      console.error('❌ Error enviando token:', error);
    }
  }

  /**
   * Manejar notificación recibida cuando app está en foreground
   */
  handleForegroundNotification(notification) {
    console.log('📬 Notificación en foreground:', notification.title);
    
    // Aquí puedes mostrar un toast o modal
    // Ejemplo: usar tu componente de toast existente
    
    // También puedes disparar un evento personalizado
    const event = new CustomEvent('pushNotificationReceived', {
      detail: notification
    });
    window.dispatchEvent(event);
  }

  /**
   * Manejar tap en notificación (navegar al contenido)
   */
  handleNotificationTap(notification) {
    console.log('👆 Navegando desde notificación:', notification.data);
    
    // Extraer datos de navegación
    const { articleId, section, url } = notification.data;
    
    if (url) {
      // Deep link completo
      window.location.href = url;
    } else if (articleId && section) {
      // Navegar a artículo específico
      const sectionSlug = this.createSlug(section);
      const articleSlug = notification.data.slug || articleId;
      window.location.href = `/${sectionSlug}/${articleId}/${articleSlug}`;
    } else {
      // Ir al inicio
      window.location.href = '/';
    }
  }

  /**
   * Crear slug desde texto
   */
  createSlug(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Obtener lista de notificaciones entregadas
   */
  async getDeliveredNotifications() {
    if (!this.isNativePlatform) return [];
    
    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications;
    } catch (error) {
      console.error('❌ Error obteniendo notificaciones:', error);
      return [];
    }
  }

  /**
   * Remover notificaciones entregadas
   */
  async removeDeliveredNotifications(notifications) {
    if (!this.isNativePlatform) return;
    
    try {
      await PushNotifications.removeDeliveredNotifications(notifications);
      console.log('🗑️ Notificaciones removidas');
    } catch (error) {
      console.error('❌ Error removiendo notificaciones:', error);
    }
  }

  /**
   * Remover todas las notificaciones
   */
  async removeAllDeliveredNotifications() {
    if (!this.isNativePlatform) return;
    
    try {
      await PushNotifications.removeAllDeliveredNotifications();
      console.log('🗑️ Todas las notificaciones removidas');
    } catch (error) {
      console.error('❌ Error removiendo notificaciones:', error);
    }
  }

  /**
   * Verificar permisos actuales
   */
  async checkPermissions() {
    if (!this.isNativePlatform) {
      return { receive: 'prompt' };
    }
    
    try {
      return await PushNotifications.checkPermissions();
    } catch (error) {
      console.error('❌ Error verificando permisos:', error);
      return { receive: 'prompt' };
    }
  }

  /**
   * Solicitar permisos de notificaciones
   */
  async requestPermissions() {
    if (!this.isNativePlatform) {
      console.log('📱 Push notifications solo en apps nativas');
      return { receive: 'denied' };
    }
    
    try {
      return await PushNotifications.requestPermissions();
    } catch (error) {
      console.error('❌ Error solicitando permisos:', error);
      return { receive: 'denied' };
    }
  }

  // =============================================
  // Historial local de notificaciones
  // =============================================

  saveNotification(notification, read = false) {
    try {
      const history = this.getNotificationHistory();
      
      const title = notification.title || notification.data?.title || '';
      const body = notification.body || notification.data?.body || notification.data?.message || '';
      const data = notification.data || {};
      const hasArticle = data.articleId || data.url;

      if (!title && !body && !hasArticle) return;

      const tenMinAgo = Date.now() - (10 * 60 * 1000);

      // Buscar entrada existente por contenido o por artículo vinculado
      const existingIdx = history.findIndex(n => {
        const recent = new Date(n.receivedAt).getTime() > tenMinAgo;
        if (!recent) return false;

        if (title && n.title) {
          const fp1 = `${title}|${body}`.toLowerCase().trim();
          const fp2 = `${n.title}|${n.body}`.toLowerCase().trim();
          if (fp1 === fp2) return true;
        }

        if (hasArticle && n.data?.articleId && n.data.articleId === data.articleId) return true;

        return false;
      });

      if (existingIdx !== -1) {
        // Fusionar: actualizar entrada existente con datos nuevos
        const prev = history[existingIdx];
        history[existingIdx] = {
          ...prev,
          title: title || prev.title,
          body: body || prev.body,
          data: { ...prev.data, ...data },
          read: read || prev.read
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
        window.dispatchEvent(new CustomEvent('notificationHistoryUpdated'));
        return;
      }

      const id = notification.id || `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

      const entry = {
        id,
        title,
        body,
        image: data.imageUrl || data.image || null,
        data,
        receivedAt: new Date().toISOString(),
        read
      };

      history.unshift(entry);

      const trimmed = history.slice(0, MAX_NOTIFICATIONS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      
      window.dispatchEvent(new CustomEvent('notificationHistoryUpdated'));
    } catch (error) {
      console.error('Error guardando notificación en historial:', error);
    }
  }

  getNotificationHistory() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      
      const history = JSON.parse(raw);
      const cutoff = Date.now() - (MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
      
      return history.filter(n => new Date(n.receivedAt).getTime() > cutoff);
    } catch (error) {
      console.error('Error leyendo historial de notificaciones:', error);
      return [];
    }
  }

  markAsRead(notificationId) {
    try {
      const history = this.getNotificationHistory();
      const updated = history.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('notificationHistoryUpdated'));
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  }

  markAllAsRead() {
    try {
      const history = this.getNotificationHistory();
      const updated = history.map(n => ({ ...n, read: true }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('notificationHistoryUpdated'));
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  }

  getUnreadCount() {
    return this.getNotificationHistory().filter(n => !n.read).length;
  }

  clearHistory() {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('notificationHistoryUpdated'));
  }
}

// Crear instancia singleton
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;

